import React, { useEffect, useMemo, useState } from "react";
import { api, peso, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Send, Trash2, XCircle, CreditCard, Ban, Percent, Table2, Hash, Users, Ticket, User, Star, AlertTriangle } from "lucide-react";
const uuid = () => (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));

export default function POSPage() {
  const { branchId, user, hasPerm } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [order, setOrder] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    api.get("/tables", { params: { branch_id: branchId } }).then(({data}) => setTables(data)).catch(()=>{});
    api.get("/categories").then(({data}) => { setCategories(data); if (data.length) setActiveCat(data[0].id); }).catch(()=>{});
    api.get("/products", { params: { active_only: true } }).then(({data}) => setProducts(data)).catch(()=>{});
  }, [branchId]);

  // Hydrate from ?order=<id> — e.g. Host page opened a POS tab and navigated
  // here. We fetch once and hop off the query string so refresh doesn't loop.
  useEffect(() => {
    const orderId = searchParams.get("order");
    if (!orderId || order?.id === orderId) return;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setOrder(data);
        if (data.table_id) {
          const { data: tbls } = await api.get("/tables", { params: { branch_id: data.branch_id } });
          setSelectedTable(tbls.find((t) => t.id === data.table_id) || null);
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [searchParams, order?.id]);

  const activeProducts = useMemo(
    () => products.filter((p) => (!activeCat || p.category_id === activeCat) && p.active !== false),
    [products, activeCat]
  );

  const createOrder = async (table) => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        branch_id: branchId,
        order_type: table ? "table_service" : "bar_counter",
        table_id: table?.id || null,
        server_user_id: user?.id,
      });
      setOrder(data);
      setSelectedTable(table || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const addItem = async (product) => {
    if (!order) { toast.error("Start an order first"); return; }
    try {
      const { data } = await api.post(`/orders/${order.id}/items`, {
        items: [{ product_id: product.id, quantity: 1, modifiers: [], notes: "" }],
      });
      setOrder(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitOrder = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/submit`);
      setOrder(data);
      toast.success(`Order ${data.order_number} sent to KDS`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const voidItem = async (line_id) => {
    const reason = prompt("Void reason?");
    if (!reason) return;
    try {
      const { data } = await api.post(`/orders/${order.id}/void-item/${line_id}`, null, { params: { reason } });
      const refreshed = await api.get(`/orders/${order.id}`);
      setOrder(refreshed.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applyDiscount = async () => {
    const percent = parseFloat(prompt("Discount percent (0-100)?", "10"));
    if (!percent || percent <= 0 || percent > 100) return;
    const reason = prompt("Reason?") || "Manager approval";
    try {
      await api.post(`/orders/${order.id}/discount`, { reason, percent });
      const refreshed = await api.get(`/orders/${order.id}`);
      setOrder(refreshed.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const clearOrder = () => {
    setOrder(null);
    setSelectedTable(null);
    if (searchParams.get("order")) {
      searchParams.delete("order");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const activeItems = (order?.items || []).filter((i) => i.status !== "voided");
  const totals = order?.totals || {};

  return (
    <div className="h-[calc(100vh-64px)] flex" data-testid="pos-page">
      {/* Product area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Table + category bar */}
        <div className="p-4 border-b border-white/10 bg-zinc-950/50">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tiny-label mr-2">Tables:</span>
            {tables.slice(0, 20).map((t) => (
              <button
                key={t.id}
                data-testid={`pos-table-${t.name}`}
                onClick={() => createOrder(t)}
                disabled={!!order}
                className={`h-10 px-3 rounded-lg text-xs font-bold border transition-colors ${
                  t.status === "occupied" ? "bg-violet-500/15 border-violet-500/30 text-violet-200"
                  : t.status === "reserved" ? "bg-amber-500/15 border-amber-500/30 text-amber-200"
                  : t.status === "cleaning" ? "bg-orange-500/15 border-orange-500/30 text-orange-300"
                  : "bg-zinc-900 border-white/10 text-zinc-300 hover:border-emerald-500/40"
                } ${order ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
              >
                {t.name}
              </button>
            ))}
            <button
              onClick={() => createOrder(null)}
              disabled={!!order || !branchId}
              data-testid="pos-bar-counter-btn"
              className="h-10 px-4 rounded-lg text-xs font-bold border border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 transition-colors disabled:opacity-40"
            >
              + Bar counter
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`pos-cat-${c.name}`}
                onClick={() => setActiveCat(c.id)}
                className={`h-10 px-4 rounded-lg text-sm font-semibold transition-colors ${
                  activeCat === c.id ? "bg-violet-600 text-white" : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-white/10"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          {!branchId && (
            <div className="panel p-8 text-center text-zinc-400" data-testid="pos-branch-hint">
              Select a branch from the top nav to open a POS session.
            </div>
          )}
          {branchId && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {activeProducts.map((p) => (
                <button
                  key={p.id}
                  data-testid={`pos-product-${p.sku || p.name}`}
                  onClick={() => addItem(p)}
                  disabled={!order || p.sold_out}
                  className="text-left h-32 p-4 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-white/5 hover:border-violet-500/40 transition-colors active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex flex-col justify-between"
                >
                  <div>
                    <div className="text-white font-bold text-base leading-tight line-clamp-2">{p.name}</div>
                    <div className="tiny-label mt-1">{p.prep_station}</div>
                  </div>
                  <div className="flex items-end justify-between">
                    <span className="text-lg font-extrabold text-violet-300">{peso(p.price)}</span>
                    {p.sold_out && <span className="tiny-label text-red-400">SOLD OUT</span>}
                  </div>
                </button>
              ))}
              {branchId && activeProducts.length === 0 && (
                <div className="col-span-full text-center text-zinc-500 py-12">No products in this category.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cart */}
      <aside className="w-[420px] shrink-0 bg-zinc-950 border-l border-white/10 flex flex-col" data-testid="pos-cart">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-start justify-between">
            <div>
              <div className="tiny-label">Current order</div>
              <div className="mt-1 flex items-center gap-2">
                <Hash size={14} className="text-violet-400" />
                <span className="mono text-white font-bold">{order?.order_number || "—"}</span>
                {order && <StatusBadge status={order.status} />}
              </div>
            </div>
            {order && <button onClick={clearOrder} className="text-zinc-500 hover:text-red-400" data-testid="pos-close-order-btn"><XCircle size={18}/></button>}
          </div>
          {selectedTable && (
            <div className="mt-2 flex items-center gap-2 text-sm text-zinc-400">
              <Table2 size={14} /> {selectedTable.name} · {selectedTable.zone} · cap {selectedTable.capacity}
            </div>
          )}
          {order?.reservation_id && (
            <div className="mt-3 p-3 rounded-lg border border-violet-500/40 bg-violet-500/5 text-xs space-y-1"
                  data-testid="pos-reservation-banner">
              <div className="flex items-center gap-2 text-violet-300 font-bold uppercase tracking-widest text-[10px]">
                <Ticket size={12}/> Reservation
                {order.reservation_confirmation_code && (
                  <span className="mono text-white/80 normal-case tracking-normal">
                    {order.reservation_confirmation_code}
                  </span>
                )}
                {order.vip_package_snapshot && (
                  <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border text-amber-300 border-amber-500/40 bg-amber-500/10 inline-flex items-center gap-1"
                        data-testid="pos-vip-badge">
                    <Star size={10}/> {order.vip_package_snapshot.code}
                  </span>
                )}
              </div>
              <div className="text-white/90 font-semibold text-sm">{order.customer_name || "—"}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-zinc-400">
                <span className="flex items-center gap-1"><Users size={11}/> {order.guest_count} pax</span>
                {order.promoter_name && (
                  <span className="flex items-center gap-1"><User size={11}/> Promoter: <span className="text-white/80">{order.promoter_name}</span></span>
                )}
                {(order.totals?.deposit_credit_total ?? 0) > 0 && (
                  <span className="flex items-center gap-1">
                    <CreditCard size={11}/> Deposit credit
                    <span className="text-emerald-300 font-bold">{peso(order.totals.deposit_credit_total)}</span>
                    {(order.totals?.deposit_credit_applied ?? 0) > 0 && (
                      <span className="text-white/60">· applied {peso(order.totals.deposit_credit_applied)}</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}
          {order && (order.minimum_spend_total ?? 0) > 0 && (
            <div className={`mt-3 p-3 rounded-lg border text-xs ${
              order.totals?.minimum_spend_met
                ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300"
                : "border-amber-500/40 bg-amber-500/5 text-amber-300"
            }`} data-testid="pos-min-spend-banner">
              <div className="flex items-center gap-2 uppercase tracking-widest text-[10px] font-bold">
                {order.totals?.minimum_spend_met ? <Star size={12}/> : <AlertTriangle size={12}/>}
                Minimum spend
                <span className="ml-auto mono normal-case tracking-normal">
                  {peso(order.totals?.minimum_spend_eligible_sales ?? 0)} / {peso(order.minimum_spend_total)}
                </span>
              </div>
              {!order.totals?.minimum_spend_met && (
                <div className="mt-1 text-white/80" data-testid="pos-min-spend-remaining">
                  <span className="font-bold text-amber-300">{peso(order.totals?.minimum_spend_remaining ?? 0)}</span>
                  {" "}remaining to unlock settlement
                </div>
              )}
              {order.totals?.minimum_spend_met && (
                <div className="mt-1 text-white/70">Package requirements met — settlement unlocked.</div>
              )}
            </div>
          )}
          {order?.bottle_service && (
            <BottleServicePanel order={order} onChanged={(o) => setOrder(o)}/>
          )}
          {order?.reservation_id && (
        <TableServicePanel order={order} onChanged={(o) => setOrder(o)}/>
      )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {!order && <div className="text-center text-zinc-500 py-16">Choose a table or start a bar counter order.</div>}
          {order && activeItems.length === 0 && <div className="text-center text-zinc-500 py-16">Cart is empty. Tap products to add.</div>}
          {order && activeItems.map((it) => (
            <div key={it.line_id} className="p-3 rounded-lg border border-white/5 bg-zinc-900" data-testid={`cart-line-${it.line_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="text-sm font-bold text-white">{it.name}</div>
                  <div className="tiny-label">{it.quantity} × {peso(it.price)} · {it.prep_station}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-white">{peso(it.line_total ?? it.price * it.quantity)}</div>
                  {hasPerm("order.void") && (
                    <button onClick={() => voidItem(it.line_id)} className="mt-1 text-red-400/70 hover:text-red-400" data-testid={`cart-void-${it.line_id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-5 border-t border-white/10 space-y-2 bg-zinc-950">
          <Row label="Subtotal" value={peso(totals.subtotal)} />
          <Row label={`Service charge (${Math.round((totals.service_charge_rate || 0)*100)}%)`} value={peso(totals.service_charge)} />
          <Row label={`Tax (${Math.round((totals.tax_rate || 0)*100)}%)`} value={peso(totals.tax)} />
          {(totals.discount ?? 0) > 0 && <Row label="Discount" value={`- ${peso(totals.discount)}`} accent="text-amber-300" />}
          <div className="pt-2 mt-2 border-t border-white/10 flex items-center justify-between">
            <span className="text-white font-bold">Total</span>
            <span className="text-2xl font-extrabold text-white" data-testid="cart-total">{peso(totals.total)}</span>
          </div>
          {(totals.deposit_credit_applied ?? 0) > 0 && (
            <>
              <Row label="Deposit credit applied"
                    value={`- ${peso(totals.deposit_credit_applied)}`}
                    accent="text-emerald-300"/>
              <div className="pt-1 flex items-center justify-between">
                <span className="text-white/80 text-sm font-semibold">Balance due</span>
                <span className="text-lg font-extrabold text-emerald-300" data-testid="cart-balance-due">{peso(totals.balance_due ?? Math.max(totals.total - totals.deposit_credit_applied, 0))}</span>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-2 pt-3">
            <button
              disabled={!order || activeItems.length === 0 || busy}
              onClick={submitOrder}
              data-testid="pos-submit-btn"
              className="btn-touch bg-violet-600 hover:bg-violet-500 text-white flex items-center justify-center gap-2 disabled:opacity-40 shadow-[0_0_20px_rgba(139,92,246,0.35)]"
            >
              <Send size={16}/> Send to KDS
            </button>
            <button
              disabled={!order || !["submitted","in_preparation","served","partially_served","ready_for_payment","partially_paid"].includes(order?.status) || busy || (order?.totals?.balance_due ?? 1) <= 0.01}
              onClick={() => setPayOpen(true)}
              data-testid="pos-pay-btn"
              className="btn-touch bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <CreditCard size={16}/> Payment
            </button>
            {order && ["submitted","in_preparation","served","partially_served","ready_for_payment","partially_paid"].includes(order.status)
              && (order.totals?.balance_due ?? 1) <= 0.01
              && (order.totals?.deposit_credit_total ?? 0) > 0
              && hasPerm("payment.process") && (
              <button
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data } = await api.post(`/orders/${order.id}/settle-zero-balance`, {});
                    const refreshed = await api.get(`/orders/${order.id}`);
                    setOrder(refreshed.data);
                    setPayOpen(false);
                    const s = data.settlement;
                    if (s?.settlement_status === "complete") {
                      toast.success("Reservation completed · Table sent to cleaning");
                    } else if (s?.settlement_status === "requires_repair") {
                      toast.error("Order paid · settlement needs manager review");
                    } else {
                      toast.success("Order paid");
                    }
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  finally { setBusy(false); }
                }}
                data-testid="pos-settle-deposit-btn"
                className="btn-touch bg-amber-600 hover:bg-amber-500 text-white flex items-center justify-center gap-2 disabled:opacity-40"
                disabled={busy || (order.minimum_spend_total > 0 && !order.totals?.minimum_spend_met)}
                title={order.minimum_spend_total > 0 && !order.totals?.minimum_spend_met
                  ? `Minimum spend not met — ${peso(order.totals?.minimum_spend_remaining || 0)} remaining` : ""}
              >
                <CreditCard size={16}/> Settle Using Deposit
              </button>
            )}
            {hasPerm("discount.apply") && (
              <button onClick={applyDiscount} disabled={!order || activeItems.length===0} className="h-11 rounded-lg bg-zinc-900 border border-white/10 text-white hover:bg-zinc-800 flex items-center justify-center gap-2 text-sm font-semibold" data-testid="pos-discount-btn">
                <Percent size={14}/> Discount
              </button>
            )}
            <button onClick={clearOrder} className="h-11 rounded-lg bg-zinc-900 border border-white/10 text-zinc-300 hover:bg-zinc-800 flex items-center justify-center gap-2 text-sm font-semibold" data-testid="pos-clear-btn">
              <Ban size={14}/> Clear
            </button>
          </div>
        </div>
      </aside>

      {payOpen && order && (
        <PaymentDrawer order={order} onClose={() => setPayOpen(false)} onPaid={(o) => { setOrder(o); if (o.status === "paid") { toast.success("Payment complete."); setPayOpen(false); } }} />
      )}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-zinc-400">{label}</span>
      <span className={`font-semibold ${accent || "text-zinc-200"}`}>{value}</span>
    </div>
  );
}


function TableServicePanel({ order, onChanged }) {
  const [busy, setBusy] = React.useState("");
  const [dialog, setDialog] = React.useState(null); // 'transfer' | 'attach' | 'server' | 'guests' | 'request' | null
  const [tables, setTables] = React.useState([]);
  const [staff, setStaff] = React.useState([]);
  const linked = order.linked_table_ids || [];
  const reqs = order.service_requests || [];
  const pending = reqs.filter((r) => r.status === "pending");
  const opLocked = ["in_progress", "requires_repair"].includes(order.table_operation_status);

  const refresh = async () => {
    const fresh = await api.get(`/orders/${order.id}`);
    onChanged(fresh.data);
  };

  const openTransfer = async () => {
    try {
      const { data } = await api.get("/tables", { params: { branch_id: order.branch_id } });
      setTables(data.filter((t) => t.status === "available" && t.id !== order.table_id));
      setDialog("transfer");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openAttach = async () => {
    try {
      const { data } = await api.get("/tables", { params: { branch_id: order.branch_id } });
      const excluded = new Set([order.table_id, ...linked]);
      setTables(data.filter((t) => t.status === "available" && !excluded.has(t.id)));
      setDialog("attach");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openServer = async () => {
    try {
      const { data } = await api.get("/team/users", { params: { branch_id: order.branch_id } }).catch(async () => await api.get("/users"));
      setStaff((data || []).filter((u) => u.active !== false));
      setDialog("server");
    } catch (e) { setStaff([]); setDialog("server"); }
  };

  const doTransfer = async (targetId) => {
    const reason = window.prompt("Reason for transfer?", "Guest requested larger table");
    if (!reason) return;
    setBusy(`transfer-${targetId}`);
    try {
      await api.post(`/orders/${order.id}/table-transfer`, {
        target_table_id: targetId, reason, idempotency_key: uuid(),
      });
      toast.success("Table transferred");
      setDialog(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const doAttach = async (tableId) => {
    const reason = window.prompt("Reason for adding companion table?", "Party grew");
    if (!reason) return;
    setBusy(`attach-${tableId}`);
    try {
      await api.post(`/orders/${order.id}/companion-tables`, {
        table_id: tableId, reason, idempotency_key: uuid(),
      });
      toast.success("Companion table attached");
      setDialog(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const doDetach = async (tableId) => {
    const reason = window.prompt("Reason for releasing companion table?", "Party downsized");
    if (!reason) return;
    setBusy(`detach-${tableId}`);
    try {
      await api.delete(`/orders/${order.id}/companion-tables/${tableId}`, {
        data: { reason, idempotency_key: uuid() },
      });
      toast.success("Companion table released");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const doServerChange = async (userId) => {
    const reason = window.prompt("Reason for server change?", "Shift handover");
    if (!reason) return;
    setBusy(`server-${userId}`);
    try {
      await api.patch(`/orders/${order.id}/service-controls`, {
        server_user_id: userId, reason,
      });
      toast.success("Server reassigned");
      setDialog(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const doGuestCount = async () => {
    const raw = window.prompt("Update guest count", String(order.guest_count || 1));
    if (!raw) return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) { toast.error("Enter a valid guest count"); return; }
    const reason = window.prompt("Reason?", "Party size updated") || "Party size updated";
    setBusy("guests");
    try {
      await api.patch(`/orders/${order.id}/service-controls`, {
        guest_count: n, reason,
      });
      toast.success("Guest count updated");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const doRequest = async (type) => {
    const notes = window.prompt(`Notes for ${type} request (optional)`, "") || "";
    setBusy(`req-${type}`);
    try {
      await api.post(`/orders/${order.id}/service-requests`, {
        type, notes, idempotency_key: uuid(),
      });
      toast.success(`${type.replace("_", " ")} requested`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const REQ_TYPES = [
    { key: "ice", label: "Ice" },
    { key: "mixers", label: "Mixers" },
    { key: "glassware", label: "Glassware" },
    { key: "cleanup", label: "Cleanup" },
    { key: "server_assistance", label: "Server" },
    { key: "bottle_presentation", label: "Bottle show" },
  ];

  return (
    <div className="mt-3 p-3 rounded-lg border border-violet-500/30 bg-violet-500/5"
          data-testid="pos-table-service">
      <div className="flex items-center gap-2 text-violet-300 font-bold uppercase tracking-widest text-[10px]">
        <Table2 size={12}/> Table Service
        {opLocked && (
          <span className="ml-auto text-[9px] uppercase tracking-widest text-amber-300"
                data-testid="pos-table-op-locked">
            {order.table_operation_status === "requires_repair" ? "REPAIR NEEDED" : "OP IN PROGRESS"}
          </span>
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Primary</div>
          <div className="mono text-white">{order.table_id?.slice(-6) || "—"}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Companions</div>
          <div className="mono text-white flex flex-wrap gap-1">
            {linked.length === 0 && "—"}
            {linked.map((t) => (
              <span key={t} className="inline-flex items-center gap-1"
                    data-testid={`pos-companion-${t.slice(-6)}`}>
                {t.slice(-6)}
                <button
                  onClick={() => doDetach(t)}
                  disabled={opLocked || busy === `detach-${t}`}
                  data-testid={`pos-detach-${t.slice(-6)}`}
                  className="text-red-300 hover:text-red-200 disabled:opacity-30"
                  title="Release companion"
                >×</button>
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Guests</div>
          <div className="mono text-white">{order.guest_count || "—"}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending reqs</div>
          <div className="mono text-white">{pending.length}</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <button onClick={openTransfer} disabled={opLocked} data-testid="pos-svc-transfer-btn"
                className="h-8 rounded-md bg-zinc-900 border border-white/10 text-zinc-200 hover:border-violet-500/50 disabled:opacity-40">
          Transfer table
        </button>
        <button onClick={openAttach} disabled={opLocked} data-testid="pos-svc-attach-btn"
                className="h-8 rounded-md bg-zinc-900 border border-white/10 text-zinc-200 hover:border-violet-500/50 disabled:opacity-40">
          + Companion
        </button>
        <button onClick={openServer} disabled={opLocked} data-testid="pos-svc-server-btn"
                className="h-8 rounded-md bg-zinc-900 border border-white/10 text-zinc-200 hover:border-violet-500/50 disabled:opacity-40">
          Change server
        </button>
        <button onClick={doGuestCount} disabled={opLocked || busy === "guests"} data-testid="pos-svc-guests-btn"
                className="h-8 rounded-md bg-zinc-900 border border-white/10 text-zinc-200 hover:border-violet-500/50 disabled:opacity-40">
          Update guests
        </button>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {REQ_TYPES.map((r) => (
          <button key={r.key}
            onClick={() => doRequest(r.key)}
            disabled={busy === `req-${r.key}`}
            data-testid={`pos-svc-req-${r.key}`}
            className="h-7 px-2.5 rounded-md bg-zinc-900 border border-white/10 text-[10px] uppercase tracking-widest text-zinc-300 hover:border-emerald-500/40 disabled:opacity-30">
            {r.label}
          </button>
        ))}
      </div>

      {pending.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs border-t border-white/5 pt-2">
          {pending.map((r) => (
            <li key={r.request_id} className="flex items-center gap-2"
                data-testid={`pos-svc-pending-${r.request_id.slice(-6)}`}>
              <span className="uppercase tracking-widest text-[9px] text-violet-300">{r.type}</span>
              <span className="text-zinc-300 flex-1 truncate">{r.notes || "—"}</span>
              <button
                onClick={async () => {
                  try {
                    await api.patch(`/orders/${order.id}/service-requests/${r.request_id}`,
                                   { status: "completed" });
                    await refresh();
                    toast.success("Request completed");
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                data-testid={`pos-svc-done-${r.request_id.slice(-6)}`}
                className="text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-200"
              >done</button>
            </li>
          ))}
        </ul>
      )}

      {dialog === "transfer" && (
        <PickerDialog
          title="Transfer to which table?"
          items={tables}
          testid="pos-transfer-dialog"
          itemLabel={(t) => `${t.name} · cap ${t.capacity}${t.vip ? " · VIP" : ""}`}
          itemTestId={(t) => `pos-transfer-pick-${t.name}`}
          onPick={(t) => doTransfer(t.id)}
          onClose={() => setDialog(null)}
          empty="No available tables."
        />
      )}
      {dialog === "attach" && (
        <PickerDialog
          title="Attach which companion?"
          items={tables}
          testid="pos-attach-dialog"
          itemLabel={(t) => `${t.name} · cap ${t.capacity}${t.vip ? " · VIP" : ""}`}
          itemTestId={(t) => `pos-attach-pick-${t.name}`}
          onPick={(t) => doAttach(t.id)}
          onClose={() => setDialog(null)}
          empty="No available tables."
        />
      )}
      {dialog === "server" && (
        <PickerDialog
          title="Assign server"
          items={staff}
          testid="pos-server-dialog"
          itemLabel={(u) => `${u.name || u.email}${u.role ? ` · ${u.role}` : ""}`}
          itemTestId={(u) => `pos-server-pick-${u.id}`}
          onPick={(u) => doServerChange(u.id)}
          onClose={() => setDialog(null)}
          empty="No staff available."
        />
      )}
    </div>
  );
}

function PickerDialog({ title, items, testid, itemLabel, itemTestId, onPick, onClose, empty }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
      <div className="relative w-full max-w-sm bg-zinc-950 border border-white/10 rounded-xl p-4 shadow-2xl max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-bold text-sm">{title}</h4>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid={`${testid}-close`}><XCircle size={16}/></button>
        </div>
        {(!items || items.length === 0) ? (
          <div className="text-xs text-zinc-500 py-6 text-center">{empty}</div>
        ) : (
          <ul className="space-y-1">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  onClick={() => onPick(it)}
                  data-testid={itemTestId(it)}
                  className="w-full text-left h-10 px-3 rounded-md bg-zinc-900 hover:bg-violet-500/10 hover:border-violet-500/40 border border-white/5 text-sm text-zinc-200 flex items-center">
                  {itemLabel(it)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function BottleServicePanel({ order, onChanged }) {  const [busy, setBusy] = React.useState("");
  const bs = order.bottle_service || {};
  const inclusions = bs.inclusions || [];
  const activeReleases = (bs.release_history || []).filter((h) => h.status === "released");

  const release = async (inc) => {
    if (inc.legacy_unmapped || inc.remaining_quantity <= 0) return;
    setBusy(inc.product_id);
    try {
      await api.post(`/orders/${order.id}/bottle-service/release`, {
        product_id: inc.product_id,
        quantity: 1,
        idempotency_key: uuid(),
      });
      const refreshed = await api.get(`/orders/${order.id}`);
      onChanged(refreshed.data);
      toast.success(`Released ${inc.product_name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const cancel = async (releaseId, productName) => {
    const reason = window.prompt(`Cancel release of ${productName}? Reason:`);
    if (!reason) return;
    setBusy(releaseId);
    try {
      await api.post(`/orders/${order.id}/bottle-service/releases/${releaseId}/cancel`,
                     { reason });
      const refreshed = await api.get(`/orders/${order.id}`);
      onChanged(refreshed.data);
      toast.success("Release cancelled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5"
          data-testid="pos-bottle-service">
      <div className="flex items-center gap-2 text-amber-300 font-bold uppercase tracking-widest text-[10px]">
        <Star size={12}/> VIP Package Inclusions · {bs.package_code}
      </div>
      <ul className="mt-2 space-y-2">
        {inclusions.length === 0 && (
          <li className="text-xs text-zinc-500">No inclusions on this package.</li>
        )}
        {inclusions.map((inc, i) => {
          const disabled = inc.legacy_unmapped || inc.remaining_quantity <= 0 || busy === inc.product_id;
          const key = inc.product_id || `legacy-${i}`;
          return (
            <li key={key}
                data-testid={`pos-bs-inc-${inc.product_id || `legacy${i}`}`}
                className="flex items-center gap-3 text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold truncate">{inc.product_name}</div>
                <div className="text-zinc-400 mono">
                  {inc.included_quantity} included · {inc.released_quantity} released · {inc.remaining_quantity} remaining
                </div>
                {inc.legacy_unmapped && (
                  <div className="text-[10px] uppercase tracking-widest text-amber-300 mt-0.5">
                    Legacy inclusion · product mapping required
                  </div>
                )}
              </div>
              <button
                onClick={() => release(inc)}
                disabled={disabled}
                data-testid={`pos-bs-release-${inc.product_id || `legacy${i}`}`}
                className="btn-touch h-9 px-3 text-xs bg-amber-600 hover:bg-amber-500 text-white rounded-md flex items-center gap-1 disabled:opacity-30"
              >
                {busy === inc.product_id ? "…" : "RELEASE"}
              </button>
            </li>
          );
        })}
      </ul>
      {activeReleases.length > 0 && (
        <details className="mt-3 border-t border-white/5 pt-2">
          <summary className="text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer">
            {activeReleases.length} release{activeReleases.length === 1 ? "" : "s"} — cancel while ticket is new
          </summary>
          <ul className="mt-2 space-y-1 text-xs">
            {activeReleases.map((h) => (
              <li key={h.release_id} className="flex items-center gap-2"
                   data-testid={`pos-bs-release-row-${h.release_id.slice(-6)}`}>
                <span className="text-zinc-300 flex-1 truncate">
                  {h.quantity} × {h.product_name}
                </span>
                <button
                  onClick={() => cancel(h.release_id, h.product_name)}
                  disabled={busy === h.release_id}
                  data-testid={`pos-bs-cancel-${h.release_id.slice(-6)}`}
                  className="text-[10px] uppercase tracking-widest text-red-300 hover:text-red-200 disabled:opacity-30"
                >
                  cancel
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function PaymentDrawer({ order, onClose, onPaid }) {
  // Phase 2B closure §1 — trust server-computed balance_due (deposit-aware).
  const total = order.totals?.total || 0;
  const depositApplied = order.totals?.deposit_credit_applied ?? 0;
  const paid = order.amount_paid || 0;
  const remaining = order.totals?.balance_due ?? Math.max(0, total - depositApplied - paid);

  // Phase 3A closure — partial payments are allowed while minimum spend is
  // unmet. Only the finalizing payment is blocked. Compute the max we'll let
  // the operator enter so the drawer can't accidentally settle the tab.
  const minTotal = order.minimum_spend_total || 0;
  const minMet = !!order.totals?.minimum_spend_met || minTotal <= 0;
  const minRemaining = order.totals?.minimum_spend_remaining ?? 0;
  const maxAllowed = minMet ? remaining : Math.max(0, +(remaining - 0.02).toFixed(2));

  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState((minMet ? remaining : maxAllowed).toFixed(2));
  const [tendered, setTendered] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const parsedAmount = parseFloat(amount);
  const wouldFullySettle = !isNaN(parsedAmount) && (remaining - parsedAmount) < 0.01;
  const clientBlock = !minMet && wouldFullySettle;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        method,
        amount: parsedAmount,
        reference: reference || "",
        tendered: method === "cash" && tendered ? parseFloat(tendered) : null,
        idempotency_key: uuid(),
      };
      const { data } = await api.post(`/orders/${order.id}/payments`, payload);
      const refreshed = await api.get(`/orders/${order.id}`);
      if (data.change > 0) toast.success(`Change due: ${peso(data.change)}`);
      const s = data.settlement;
      if (s?.settlement_status === "complete") {
        toast.success("Reservation completed · Table sent to cleaning");
      } else if (s?.settlement_status === "requires_repair") {
        toast.error("Payment accepted but settlement needs manager review");
      }
      onPaid(refreshed.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const METHODS = [
    { key: "cash", label: "Cash" },
    { key: "credit_card", label: "Credit card" },
    { key: "debit_card", label: "Debit card" },
    { key: "gcash", label: "GCash" },
    { key: "maya", label: "Maya" },
    { key: "bank_transfer", label: "Bank transfer" },
    { key: "qr_payment", label: "QR" },
    { key: "complimentary", label: "Complimentary" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="payment-drawer">
      <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
      <div className="relative w-full max-w-md bg-zinc-950 border-l border-white/10 h-full p-6 flex flex-col shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="tiny-label text-violet-400">Process payment</div>
            <h3 className="text-2xl font-bold">{order.order_number}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><XCircle size={20}/></button>
        </div>

        <div className="mt-4 panel p-4">
          <div className="flex items-center justify-between">
            <span className="tiny-label">Balance due</span>
            <span className="text-3xl font-extrabold text-white" data-testid="payment-balance">{peso(remaining)}</span>
          </div>
        </div>

        {!minMet && (
          <div className="mt-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200 space-y-1"
                data-testid="payment-min-spend-warning">
            <div className="uppercase tracking-widest text-[10px] font-bold flex items-center gap-2">
              <AlertTriangle size={12}/> Minimum spend not met
            </div>
            <div>
              <span className="font-bold text-amber-100">{peso(minRemaining)}</span> remaining.
            </div>
            <div className="text-amber-100/80">
              Partial payments are allowed, but the bill cannot be fully settled yet.
              Max accepted: <span className="mono font-bold">{peso(maxAllowed)}</span>
            </div>
          </div>
        )}

        <div className="mt-4 tiny-label">Method</div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {METHODS.map((m) => (
            <button
              key={m.key}
              data-testid={`pay-method-${m.key}`}
              onClick={() => setMethod(m.key)}
              className={`h-11 rounded-lg text-sm font-semibold border transition-colors ${
                method === m.key ? "bg-violet-600 border-violet-500 text-white" : "bg-zinc-900 border-white/10 text-zinc-300 hover:bg-zinc-800"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="mt-4 tiny-label">Amount</div>
        <input value={amount} onChange={(e)=>setAmount(e.target.value)} data-testid="pay-amount" className="mt-1 h-12 w-full rounded-lg bg-zinc-950 border border-white/10 px-4 text-white text-lg font-bold focus:ring-2 focus:ring-violet-500"/>
        {clientBlock && (
          <div className="mt-2 text-xs text-amber-300" data-testid="pay-amount-warning">
            This amount would fully settle the tab, but minimum spend is not yet met.
            Enter {peso(maxAllowed)} or less.
          </div>
        )}

        {method === "cash" && (
          <>
            <div className="mt-3 tiny-label">Cash tendered (optional)</div>
            <input value={tendered} onChange={(e)=>setTendered(e.target.value)} data-testid="pay-tendered" placeholder="e.g. 2000" className="mt-1 h-12 w-full rounded-lg bg-zinc-950 border border-white/10 px-4 text-white text-lg font-bold focus:ring-2 focus:ring-violet-500"/>
          </>
        )}

        {!["cash","complimentary"].includes(method) && (
          <>
            <div className="mt-3 tiny-label">Reference number</div>
            <input value={reference} onChange={(e)=>setReference(e.target.value)} data-testid="pay-reference" placeholder="e.g. GCash txn ID" className="mt-1 h-12 w-full rounded-lg bg-zinc-950 border border-white/10 px-4 text-white focus:ring-2 focus:ring-violet-500"/>
          </>
        )}

        <div className="mt-auto pt-6">
          <button
            onClick={submit}
            disabled={busy || parseFloat(amount) <= 0 || remaining <= 0 || clientBlock}
            data-testid="pay-confirm-btn"
            className="btn-touch w-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 disabled:opacity-40 shadow-[0_0_20px_rgba(16,185,129,0.35)]"
          >
            <CreditCard size={16}/> Confirm payment
          </button>
          {remaining <= 0 && (
            <div className="mt-2 text-center text-xs text-emerald-300" data-testid="pay-zero-balance">
              Balance is zero — no payment needed.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
